import React, { useRef, useState } from 'react';
import { Upload, Check, AlertTriangle, X, Loader } from 'lucide-react';
import { API_URL } from '../../config';
import FaceCropperModal from './FaceCropperModal';

// Mappt Backend-Issue-Codes auf deutsche User-Hinweise (kompakt)
const ISSUE_LABELS = {
  blur: 'Foto ist unscharf',
  underexposed: 'Foto ist zu dunkel',
  overexposed: 'Foto ist überbelichtet',
  no_face: 'Kein Gesicht erkennbar',
  multi_face: 'Mehrere Personen im Bild',
  face_too_small: 'Gesicht zu klein im Bild',
  embedding_invalid: 'Gesichts-Erkennung fehlgeschlagen',
  not_full_body: 'Bitte den ganzen Körper zeigen',
  decode_failed: 'Foto-Format nicht lesbar',
  invalid_slot_kind: 'Interner Fehler — bitte neu laden',
  heic_conversion_failed: 'iPhone-Foto (HEIC) konnte nicht konvertiert werden',
};

// iPhone speichert Fotos als HEIC. Browser können HEIC nicht nativ rendern
// (Cropper-Modal würde leer bleiben). Daher clientseitig zu JPEG umwandeln,
// bevor das Modal aufgeht. heic2any wird dynamisch geladen, damit der normale
// JPG/PNG-Pfad die Library nicht ins Bundle zieht.
const isHeicFile = (f) => {
  const name = (f?.name || '').toLowerCase();
  const type = (f?.type || '').toLowerCase();
  return (
    name.endsWith('.heic') || name.endsWith('.heif') ||
    type.includes('heic') || type.includes('heif')
  );
};

const convertHeicToJpeg = async (file) => {
  const mod = await import('heic2any');
  const heic2any = mod.default || mod;
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const blob = Array.isArray(out) ? out[0] : out;
  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
};

const formatIssues = (issues) => {
  if (!issues || issues.length === 0) return null;
  const labels = issues.map((i) => ISSUE_LABELS[i] || i);
  return labels.join(' · ');
};

/**
 * Single Foto-Slot mit Drag&Drop, Klick-Upload, sofortiger Quality-Validation,
 * roter/grüner Border je nach Status. Lädt direkt nach Drop hoch.
 *
 * Props:
 *   slotName     — interner Slot-Name (Backend-Identifier)
 *   slotKind     — 'face_front' | 'face_side' | 'face_back' | 'body_front' | 'body_back' | 'body_side'
 *   label        — Anzeige-Text
 *   value        — { photoId, scores, fileName, previewUrl } | null
 *   onChange(slotName, value) — bei Erfolg/Reset
 */
const PhotoUploadSlot = ({ slotName, slotKind, label, required, value, onChange }) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [issues, setIssues] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingCropFile, setPendingCropFile] = useState(null);

  // Alle Slots bekommen den Cropper. Bei face_* zentriert der User auf Kopf/Schultern,
  // bei body_* auf den ganzen Körper. Aspekt 3:4 ist gleich — passt zur Slot-Vorschau.
  const needsCrop = true;
  const cropMode = typeof slotKind === 'string' && slotKind.startsWith('body_') ? 'body' : 'face';

  const upload = async (file) => {
    setIsUploading(true);
    setIssues(null);

    // Optimistische Preview (lokal, sofort sichtbar)
    const previewUrl = URL.createObjectURL(file);

    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('slot_kind', slotKind);

      const res = await fetch(`${API_URL}/api/avatar/upload-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('snapshot_token')}` },
        body: fd,
      });

      if (!res.ok) {
        const text = await res.text();
        setIssues([`server_error: ${res.status}`]);
        URL.revokeObjectURL(previewUrl);
        console.error('Upload failed', res.status, text);
        return;
      }

      const data = await res.json();
      if (data.ok) {
        // Falls vorher schon ein Foto im Slot war, das alte serverseitig wegräumen
        if (value?.photoId) {
          fetch(`${API_URL}/api/avatar/upload-photo/${value.photoId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${localStorage.getItem('snapshot_token')}` },
          }).catch(() => {});
        }
        onChange(slotName, {
          photoId: data.photo_id,
          scores: data.scores,
          fileName: file.name,
          previewUrl,
        });
        setIssues(null);
      } else {
        setIssues(data.issues || ['unknown']);
        URL.revokeObjectURL(previewUrl);
      }
    } catch (err) {
      console.error(err);
      setIssues(['network']);
      URL.revokeObjectURL(previewUrl);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePicked = async (f) => {
    if (!f) return;
    let picked = f;
    if (isHeicFile(f)) {
      setIsConverting(true);
      setIssues(null);
      try {
        picked = await convertHeicToJpeg(f);
      } catch (err) {
        console.error('HEIC conversion failed', err);
        setIssues(['heic_conversion_failed']);
        setIsConverting(false);
        return;
      }
      setIsConverting(false);
    }
    if (needsCrop) {
      setPendingCropFile(picked);
    } else {
      upload(picked);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    handlePicked(f);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handlePicked(f);
  };

  const handleCropConfirm = (croppedFile) => {
    setPendingCropFile(null);
    upload(croppedFile);
  };

  const handleCropCancel = () => {
    setPendingCropFile(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (value?.photoId) {
      fetch(`${API_URL}/api/avatar/upload-photo/${value.photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('snapshot_token')}` },
      }).catch(() => {});
    }
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange(slotName, null);
    setIssues(null);
  };

  const hasError = !!issues && issues.length > 0;
  const hasPhoto = !!value?.photoId;
  const isBusy = isUploading || isConverting;

  let borderClass = 'border-white/30 hover:border-[#10B981]/60';
  if (dragOver) borderClass = 'border-[#10B981]';
  if (hasPhoto) borderClass = 'border-[#10B981] glow-green-soft';
  if (hasError) borderClass = 'border-red-500';

  return (
    <div className="flex flex-col">
      <div
        onClick={() => !isBusy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`relative aspect-[3/4] rounded-2xl border-2 ${borderClass} bg-white/5 transition-all cursor-pointer overflow-hidden`}
      >
        {value?.previewUrl ? (
          <img
            src={value.previewUrl}
            alt={label}
            className={`absolute inset-0 w-full h-full object-cover ${hasError ? 'opacity-50' : ''}`}
          />
        ) : null}

        {/* Idle / Drop-Hint — symmetrisches 3-Zeilen-Layout (Icon · Label · Hint).
            min-h auf dem Label-Bereich verhindert Springen bei 1- vs. 2-zeiligen Labels. */}
        {/* Pflicht/Optional-Badge (nur solange der Slot leer ist) */}
        {!hasPhoto && !isBusy && (
          <div className={`absolute top-2 left-2 z-10 text-[10px] font-bold px-2 py-0.5 rounded-full pointer-events-none ${
            required ? 'bg-[#10B981] text-black' : 'bg-white/15 text-white/70'
          }`}>
            {required ? 'Pflicht' : 'Optional'}
          </div>
        )}

        {!hasPhoto && !isBusy && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2 py-3">
            <Upload className="w-8 h-8 text-white/40 mb-3 flex-shrink-0" />
            <div className="min-h-[2.6rem] flex items-center justify-center mb-2">
              <p className="text-white/80 text-sm font-semibold leading-tight">
                {label}
              </p>
            </div>
            <p className="text-white/40 text-xs">Klicken oder ziehen</p>
          </div>
        )}

        {/* Uploading-/Converting-Spinner */}
        {isBusy && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
            <Loader className="w-8 h-8 text-[#10B981] animate-spin" />
            {isConverting && (
              <p className="text-white/80 text-xs">iPhone-Foto wird konvertiert…</p>
            )}
          </div>
        )}

        {/* Erfolg-Häkchen */}
        {hasPhoto && !hasError && (
          <div className="absolute top-2 right-2 bg-[#10B981] text-black rounded-full w-7 h-7 flex items-center justify-center shadow-lg">
            <Check className="w-5 h-5" />
          </div>
        )}

        {/* Reset-Button */}
        {(hasPhoto || hasError) && !isBusy && (
          <button
            onClick={handleClear}
            className="absolute top-2 left-2 bg-black/70 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-black"
            title="Entfernen"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Label-Footer (immer sichtbar wenn Foto da) */}
        {hasPhoto && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs font-semibold px-2 py-1 text-center">
            {label}
          </div>
        )}
      </div>

      {/* Fehler-Hinweis darunter */}
      {hasError && (
        <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">
              Sorry, dieses Foto ist leider nicht in ausreichender Qualität.
            </p>
            <p className="opacity-80">
              {formatIssues(issues)} — bitte ein passendes Foto hochladen.
            </p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
        onChange={handleFileChange}
        className="sr-only"
      />

      {pendingCropFile && (
        <FaceCropperModal
          file={pendingCropFile}
          slotLabel={label}
          mode={cropMode}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default PhotoUploadSlot;
